import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Zap, Trophy, Sparkles } from 'lucide-react';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

function ActivityTicker() {
  const [events, setEvents] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pollTimer = useRef(null);
  const wsConnected = useRef(false);

  useEffect(() => {
    connectWebSocket();
    // Always start polling as initial load + fallback
    fetchEvents();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const connectWebSocket = () => {
    if (!WS_URL) {
      startPolling();
      return;
    }
    try {
      const ws = new WebSocket(`${WS_URL}/ws/activity`);
      wsRef.current = ws;

      ws.onopen = () => {
        wsConnected.current = true;
        // Stop polling since WS is connected
        if (pollTimer.current) {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
        }
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'history') {
          setEvents(data.events || []);
        } else if (data.type === 'event') {
          setEvents(prev => [data.event, ...prev].slice(0, 15));
        }
      };

      ws.onclose = () => {
        wsConnected.current = false;
        startPolling();
        // Reconnect WS after 10s
        reconnectTimer.current = setTimeout(connectWebSocket, 10000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      startPolling();
    }
  };

  const startPolling = () => {
    if (pollTimer.current) return;
    pollTimer.current = setInterval(fetchEvents, 15000);
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/activity-feed?limit=15`);
      setEvents(response.data.events || []);
    } catch (e) {
      // Non-critical
    }
  };

  const formatEvent = (event) => {
    switch (event.event_type) {
      case 'slot_purchase':
        return { icon: <Zap className="w-3 h-3 text-orange-400" />, text: `${event.username} bought slot ${event.slot_number} in ${event.game_name}` };
      case 'win':
        return { icon: <Trophy className="w-3 h-3 text-yellow-400" />, text: `${event.username} won ${event.amount?.toFixed(1)} ${event.currency || 'XRP'} in ${event.game_name}!` };
      case 'new_game':
        return { icon: <Sparkles className="w-3 h-3 text-purple-400" />, text: `${event.username} listed "${event.game_name}" (${event.prize_type})` };
      case 'bin':
        return { icon: <Trophy className="w-3 h-3 text-red-400" />, text: `${event.username} BIN'd "${event.game_name}" for ${event.amount?.toFixed(1)} ${event.currency || 'XRP'}!` };
      default:
        return null;
    }
  };

  if (events.length === 0) return null;

  return (
    <div data-testid="activity-ticker" className="w-full overflow-hidden bg-slate-800/50 border-b border-slate-700/30 py-1.5">
      <div className="flex animate-ticker whitespace-nowrap">
        {[...events, ...events].map((event, idx) => {
          const formatted = formatEvent(event);
          if (!formatted) return null;
          return (
            <span key={idx} className="inline-flex items-center gap-1.5 mx-4 text-[11px] text-slate-400 flex-shrink-0">
              {formatted.icon}
              <span>{formatted.text}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default ActivityTicker;
